import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid, Toolbar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { TextField, Autocomplete } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import ListFilter from '@ts-private/list-filter';
import * as dictionariesActions from 'actions/actionCreators/dictionariesActions';
import * as clientActions from 'features/offer/edit/client/form/actionCreators';
import * as visibilityLevels from 'constants/visibilityLevels';
import { getDateFormat, getListFilterLocalizedStrings } from 'utils/functions';
import { getTranslationByCount } from 'constants/common';
import documentTypeIds from 'constants/documentTypeIds';
import debouncePromise from 'debounce-promise';
import { debounce } from 'lodash';

const DEBOUNCE_DELAY = 500;

const FolderListTopToolbar = props => {
  const {
    filters,
    isSearchingRestrictedDealer,
    isSearchingRestrictedUserPartner,
    isSearchingRestrictedResponsible,
    visibleFilters,
    visibleFields,
    userPreferences,
    selectedUserPreference
  } = props;
  const {
    downloadEnabled,
    onUserPreferenceChanged,
    onAddUserPreference,
    onDeleteUserPreference,
    onListDeleteChanged,
    onListImplicitChanged,
    onResetFilters,
    onFilterValueChanged,
    onFilterVisibleValueChanged,
    onFieldsVisibleValueChanged
  } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const getLimitTagsText = useCallback((more, singularTag, pluralTag) => getTranslationByCount(more, singularTag, pluralTag, t), [t]);

  const handleVisibleFilterChange = name => event => onFilterVisibleValueChanged(name, event.target.checked);
  const handleVisibleFieldsChange = name => event => onFieldsVisibleValueChanged(name, event.target.checked);

  const filtersToShow = visibleFilters.filter(x => x.isVisible).map(x => x.fieldName);

  const sortableColumns = [
    {
      columnName: 'InternalFolderDate',
      displayName: t('FolderList.FolderDate')
    },
    {
      columnName: 'FolderNumberSortable',
      displayName: t('FolderList.FolderNumber')
    },
    {
      columnName: 'FinancedAmountSortable',
      displayName: t('FolderList.FinancedAmount')
    }
  ];

  const getSortableColumns = () => {
    return selectedUserPreference.order?.orderByColumns?.length > 0
      ? selectedUserPreference.order.orderByColumns.filter(x => {
          return visibleFields.find(y => x.columnName.toLowerCase().includes(y.fieldName.toLowerCase()) && y.isVisible === true);
        })
      : sortableColumns.filter(x => {
          return visibleFields.find(y => x.columnName.toLowerCase().includes(y.fieldName.toLowerCase()) && y.isVisible === true);
        });
  };

  const loadFolderTypes = debouncePromise(() => {
    const offerTypes = dispatch(dictionariesActions.loadOfferTypes(documentTypeIds.Folder))
      .then(offerTypes => {
        const facilityApprovalTypes = dispatch(dictionariesActions.loadOfferTypes(documentTypeIds.FacilityApproval));
        return Promise.all([facilityApprovalTypes, offerTypes]);
      })
      .then(([facilityApprovalTypes, offerTypes]) => {
        return offerTypes.concat(facilityApprovalTypes);
      });
    return offerTypes;
  }, DEBOUNCE_DELAY);

  const loadChannelTypes = () => dispatch(dictionariesActions.loadChannels());
  const loadPartnerTypes = () => dispatch(clientActions.loadPartnerTypes());
  const loadCurrencies = () => dispatch(dictionariesActions.loadCurrencies());
  const loadStates = () => dispatch(dictionariesActions.loadStates(documentTypeIds.Folder));
  const loadVisibilityLevels = () => dispatch(dictionariesActions.loadFolderVisibilityLevels());
  const loadRiskResponsibles = () => dispatch(dictionariesActions.loadRiskResponsibles());
  const loadDealers = input => (input ? dispatch(dictionariesActions.loadDealers(input)) : Promise.resolve([]));
  const loadRelations = () =>
    dispatch(clientActions.loadRelations()).then(relations => {
      return relations.map(r => {
        return { ...r, relationTypeId: r.id };
      });
    });
  const loadRiskAnalysisTypes = () =>
    dispatch(dictionariesActions.loadRiskAnalysisTypes(documentTypeIds.Folder)).then(risks => {
      return risks.map(r => {
        return { ...r, riskAnalysisTypeId: r.dictionaryItemId };
      });
    });
  const loadUserPartners = input =>
    input
      ? dispatch(dictionariesActions.loadUserPartners(input)).then(partners => {
          return partners.map(p => {
            return { ...p, userPartnerId: p.partnerId };
          });
        })
      : Promise.resolve([]);

  return (
    <div>
      <Toolbar>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ListFilter
              sortableColumns={getSortableColumns()}
              filters={filters}
              visibleFilters={visibleFilters}
              visibleFields={visibleFields}
              onChangeFilterValue={props.onFilterValueChanged}
              handleVisibleFilterChange={handleVisibleFilterChange}
              handleVisibleFieldsChange={handleVisibleFieldsChange}
              onDownload={props.onDownload}
              downloadEnabled={downloadEnabled}
              searchPlaceholder={t('Placeholders.FolderListSearch')}
              downloadButtonVisible={true}
              entityName={'Folder'}
              visibleUserPreferences={true}
              userPreferences={userPreferences}
              onUserPreferenceChanged={onUserPreferenceChanged}
              selectedUserPreference={selectedUserPreference}
              onAddUserPreference={onAddUserPreference}
              onListDeleteChanged={onListDeleteChanged}
              onListImplicitChanged={onListImplicitChanged}
              onDeleteUserPreference={onDeleteUserPreference}
              onResetFilters={onResetFilters}
              localizedStrings={getListFilterLocalizedStrings(t)}
            >
              <Grid container spacing={2}>
                {filtersToShow.includes('visibilityLevelId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'folderList_filters_visibilityLevel'}
                      fullWidth
                      value={filters.visibilityLevel}
                      onChange={value => onFilterValueChanged('visibilityLevel', value)}
                      label={t('FolderList.VisibilityLevel')}
                      valueKey='visibilityLevelId'
                      loadOptions={debounce(loadVisibilityLevels, DEBOUNCE_DELAY, { leading: true })}
                      isClearable
                    />
                  </Grid>
                )}
                {filtersToShow.includes('responsibleId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'folderList_filters_responsible'}
                      fullWidth
                      value={filters.responsible}
                      onChange={value => onFilterValueChanged('responsible', value)}
                      disabled={isSearchingRestrictedResponsible || filters.visibilityLevelId === visibilityLevels.MyDocuments}
                      label={t('FolderList.User')}
                      valueKey='responsibleId'
                      loadOptions={debounce(loadRiskResponsibles, DEBOUNCE_DELAY, { leading: true })}
                      isClearable
                    />
                  </Grid>
                )}
                {filtersToShow.includes('channelTypeId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'folderList_filters_channelType'}
                      fullWidth
                      value={filters.channelType}
                      onChange={value => onFilterValueChanged('channelType', value)}
                      label={t('OfferList.SalesChannel')}
                      valueKey='channelTypeId'
                      labelKey='channelTypeName'
                      loadOptions={debounce(loadChannelTypes, DEBOUNCE_DELAY, { leading: true })}
                      isClearable
                    />
                  </Grid>
                )}
                {filtersToShow.includes('clientName') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <TextField
                      id={'folderList_filters_clientName'}
                      fullWidth
                      label={t('FolderList.ClientName')}
                      value={filters.clientName || ''}
                      onChange={value => onFilterValueChanged('clientName', value)}
                    />
                  </Grid>
                )}
                {filtersToShow.includes('clientCnpOrCui') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <TextField
                      id={'folderList_filters_clientCnpOrCui'}
                      fullWidth
                      label={t('FolderList.ClientCnpOrCui')}
                      value={filters.clientCnpOrCui || ''}
                      onChange={value => onFilterValueChanged('clientCnpOrCui', value)}
                    />
                  </Grid>
                )}
                {filtersToShow.includes('partnerTypeId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'folderList_filters_partnerType'}
                      fullWidth
                      value={filters.partnerType}
                      onChange={value => onFilterValueChanged('partnerType', value)}
                      label={t('FolderList.PartnerType')}
                      valueKey='partnerTypeId'
                      labelKey='partnerTypeName'
                      loadOptions={debounce(loadPartnerTypes, DEBOUNCE_DELAY, { leading: true })}
                      isClearable
                    />
                  </Grid>
                )}
                {filtersToShow.includes('folderNumber') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <TextField
                      id={'folderList_filters_folderNumber'}
                      fullWidth
                      label={t('FolderList.FolderNumber')}
                      value={filters.folderNumber || ''}
                      onChange={value => onFilterValueChanged('folderNumber', value)}
                    />
                  </Grid>
                )}
                {filtersToShow.includes('folderStartDate') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <DateTime
                      id={'folderList_filters_folderStartDate'}
                      value={filters.folderStartDate}
                      label={t('FolderList.FolderStartDate')}
                      onChange={value => onFilterValueChanged('folderStartDate', value)}
                      isClearable
                      format={getDateFormat()}
                    />
                  </Grid>
                )}
                {filtersToShow.includes('folderEndDate') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <DateTime
                      id={'folderList_filters_folderEndDate'}
                      value={filters.folderEndDate}
                      label={t('FolderList.FolderEndDate')}
                      onChange={value => onFilterValueChanged('folderEndDate', value)}
                      isClearable
                      format={getDateFormat()}
                    />
                  </Grid>
                )}
                {filtersToShow.includes('stateIds') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'folderList_filters_stateIds'}
                      label={t('FolderList.State')}
                      valueKey='stateId'
                      fullWidth
                      isMultiSelection
                      withCheckboxes
                      limitTags={0}
                      renderTags={selected => {
                        if (selected.length === 0) return null;
                        const label = selected.length === 1 ? selected[0].name : getLimitTagsText(selected.length, 'Selection', 'Selections');
                        return <span style={theme.typography.defaultFont}>{label}</span>;
                      }}
                      value={filters.states || []}
                      onChange={value => onFilterValueChanged('states', value)}
                      loadOptions={debounce(loadStates, DEBOUNCE_DELAY, { leading: true })}
                      isClearable
                    />
                  </Grid>
                )}
                {filtersToShow.includes('offerTypeId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'folderList_filters_loadFolderType'}
                      fullWidth
                      onChange={value => onFilterValueChanged('offerType', value)}
                      value={filters.offerType}
                      label={t('FolderList.FolderType')}
                      valueKey='offerTypeId'
                      labelKey='offerTypeName'
                      loadOptions={debounce(loadFolderTypes, DEBOUNCE_DELAY, { leading: true })}
                      isClearable
                    />
                  </Grid>
                )}
                {filtersToShow.includes('riskAnalysisTypeId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'folderList_filters_riskAnalysisType'}
                      fullWidth
                      value={filters.riskAnalysisType}
                      onChange={value => onFilterValueChanged('riskAnalysisType', value)}
                      label={t('FolderList.RiskAnalysisType')}
                      valueKey='dictionaryItemId'
                      labelKey='dictionaryItemName'
                      loadOptions={debounce(loadRiskAnalysisTypes, DEBOUNCE_DELAY, { leading: true })}
                      isClearable
                    />
                  </Grid>
                )}
                {filtersToShow.includes('offerNumbers') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <TextField
                      id={'folderList_filters_offerNumbers'}
                      fullWidth
                      value={filters.offerNumbers || ''}
                      onChange={value => onFilterValueChanged('offerNumbers', value)}
                      label={t('FolderList.OfferNumbers')}
                    />
                  </Grid>
                )}
                {filtersToShow.includes('dealerId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'folderList_filters_dealer'}
                      fullWidth
                      value={filters.dealer}
                      disabled={isSearchingRestrictedDealer}
                      onChange={value => onFilterValueChanged('dealer', value)}
                      label={t('FolderList.Dealer')}
                      valueKey='dealerId'
                      loadOptions={debounce(loadDealers, DEBOUNCE_DELAY, { leading: true })}
                      isClearable
                    />
                  </Grid>
                )}
                {filtersToShow.includes('userPartnerId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'folderList_filters_userPartner'}
                      fullWidth
                      value={filters.userPartner}
                      disabled={isSearchingRestrictedUserPartner}
                      onChange={value => onFilterValueChanged('userPartner', value)}
                      label={t('FolderList.UserPartner')}
                      valueKey='partnerId'
                      loadOptions={debounce(loadUserPartners, DEBOUNCE_DELAY, { leading: true })}
                      isClearable
                    />
                  </Grid>
                )}
                {filtersToShow.includes('currencyId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'folderList_filters_currency'}
                      fullWidth
                      value={filters.currency}
                      onChange={value => onFilterValueChanged('currency', value)}
                      label={t('FolderList.Currency')}
                      valueKey='currencyId'
                      labelKey='code'
                      loadOptions={debounce(loadCurrencies, DEBOUNCE_DELAY, { leading: true })}
                      isClearable
                    />
                  </Grid>
                )}
                {filtersToShow.includes('amountFrom') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <TextField
                      id={'folderList_filters_amountFrom'}
                      fullWidth
                      isNumeric
                      label={t('FolderList.AmountFrom')}
                      value={filters.amountFrom}
                      onChange={value => onFilterValueChanged('amountFrom', value)}
                    />
                  </Grid>
                )}
                {filtersToShow.includes('amountTo') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <TextField
                      id={'folderList_filters_amountTo'}
                      fullWidth
                      isNumeric
                      label={t('FolderList.AmountTo')}
                      value={filters.amountTo}
                      onChange={value => onFilterValueChanged('amountTo', value)}
                    />
                  </Grid>
                )}
                {filtersToShow.includes('relationTypeId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'folderList_filters_relationType'}
                      fullWidth
                      value={filters.relationType}
                      onChange={value => onFilterValueChanged('relationType', value)}
                      label={t('FolderList.RelationType')}
                      valueKey='id'
                      loadOptions={debounce(loadRelations, DEBOUNCE_DELAY, { leading: true })}
                      isClearable
                    />
                  </Grid>
                )}
              </Grid>
            </ListFilter>
          </Grid>
        </Grid>
      </Toolbar>
    </div>
  );
};

FolderListTopToolbar.propTypes = {
  filters: PropTypes.shape({
    dealer: PropTypes.object,
    fullTextFilter: PropTypes.string.isRequired,
    folderNumber: PropTypes.string.isRequired,
    channelType: PropTypes.object,
    folderStartDate: PropTypes.any,
    folderEndDate: PropTypes.any,
    states: PropTypes.array,
    requestType: PropTypes.object,
    visibilityLevel: PropTypes.object,
    visibilityLevelId: PropTypes.any,
    amountTo: PropTypes.any,
    amountFrom: PropTypes.any,
    clientCnpOrCui: PropTypes.any,
    userPartner: PropTypes.object,
    clientName: PropTypes.string,
    currency: PropTypes.object,
    responsible: PropTypes.object,
    offerType: PropTypes.object,
    partnerType: PropTypes.object,
    riskAnalysisType: PropTypes.object,
    relationType: PropTypes.object,
    offerNumbers: PropTypes.any
  }),
  onFilterValueChanged: PropTypes.func.isRequired,
  onFilterVisibleValueChanged: PropTypes.func.isRequired,
  onFieldsVisibleValueChanged: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  downloadEnabled: PropTypes.bool.isRequired,
  isSearchingRestrictedDealer: PropTypes.bool,
  isSearchingRestrictedUserPartner: PropTypes.bool,
  isSearchingRestrictedResponsible: PropTypes.bool,
  visibleFilters: PropTypes.array.isRequired,
  visibleFields: PropTypes.array.isRequired,
  userPreferences: PropTypes.array,
  onUserPreferenceChanged: PropTypes.func,
  selectedUserPreference: PropTypes.object,
  onAddUserPreference: PropTypes.func,
  onDeleteUserPreference: PropTypes.func,
  onListDeleteChanged: PropTypes.func,
  onListImplicitChanged: PropTypes.func,
  onResetFilters: PropTypes.func
};

export default FolderListTopToolbar;
